import { Injectable } from '@angular/core'

import { Observable } from 'rxjs'

import { ForwardProxyMicroservice } from '@flowx/designer/shared/api/api-http-proxy.service'
import {
  AppResource,
  ApplicationResourceType,
  ProxyApiAction,
} from '@flowx/designer/shared/entities/application.entity'

import { withDefaults } from './data-model-bulk.util'
import { DataModelApiService } from './data-model.api'

import {
  BulkDataModelAttribute,
  CopyPasteEntitiesResponse,
  DataModelAttribute,
  DataModelEntity,
} from '../../modules/core/dictionary/data-model.dictionary'
import {
  BulkUpdateAttributeParams,
  CreateDataModelAttributeFromPath,
  CreateDataModelAttributeFromPathResponse,
} from '../entities/data-model.entity'

@Injectable({ providedIn: 'root' })
export class ProcessDataModelApiService extends DataModelApiService {
  private url = 'api/process/data-model'

  addAttributeFromPath(
    processResourceId: string,
    attribute: CreateDataModelAttributeFromPath
  ): Observable<AppResource<CreateDataModelAttributeFromPathResponse>> {
    return this.patch(`${this.url}/path`, attribute, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
      resourceId: processResourceId,
      action: ProxyApiAction.CREATE,
    })
  }

  bulkAddAttributesFromPath(
    processResourceId: string,
    attributes: BulkDataModelAttribute[]
  ): Observable<{ resourceContent: { data: CreateDataModelAttributeFromPathResponse[] } }> {
    return this.patch(`${this.url}/path/bulk`, withDefaults(attributes), {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
      resourceId: processResourceId,
      action: ProxyApiAction.CREATE,
    })
  }

  bulkUpdateAttributesInDataModelEntity(
    processResourceId: string,
    entityId: DataModelEntity['id'],
    attributeUpdates: BulkUpdateAttributeParams
  ): Observable<AppResource<DataModelAttribute[]>> {
    return this.patch(`${this.url}/entities/${entityId}/attributes/bulk`, attributeUpdates, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
      resourceId: processResourceId,
      action: ProxyApiAction.PARTIAL_UPDATE,
    })
  }

  copyEntities(
    processResourceId: string,
    entityIds: DataModelEntity['id'][]
  ): Observable<AppResource<CopyPasteEntitiesResponse>> {
    return this.get(
      `${this.url}/entities/copy`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        resourceId: processResourceId,
      },
      {
        params: this.addParams({ entityIds }),
      }
    )
  }

  pasteEntities(
    processResourceId: string,
    copyPasteStructure: CopyPasteEntitiesResponse,
    force: boolean = false
  ): Observable<AppResource<DataModelEntity[]>> {
    return this.patch(
      `${this.url}/entities/paste`,
      copyPasteStructure,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        resourceId: processResourceId,
        action: ProxyApiAction.CREATE,
      },
      {
        params: this.addParams({ force }),
      }
    )
  }

  autoLinkAttributes(processResourceId: string): Observable<void> {
    return this.patch(
      `${this.url}/auto-link`,
      {},
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        resourceId: processResourceId,
        action: ProxyApiAction.CREATE,
        resourceTypesToIncludeMappingFor: ['project-data-type'],
      }
    )
  }
}
